import apiClient from './apiClient';

// ==================== Vendor Directory API (Planner Feature) ====================

export const vendorAPI = {
  getCategories: async () => {
    const response = await apiClient.get('/vendors/categories');
    return response.data;
  },

  getAll: async (category = null, search = null) => {
    const params = new URLSearchParams();
    if (category && category !== 'all') params.append('category', category);
    if (search) params.append('search', search);
    const response = await apiClient.get(`/vendors?${params.toString()}`);
    return response.data;
  },

  getById: async (vendorId) => {
    const response = await apiClient.get(`/vendors/${vendorId}`);
    return response.data;
  },

  create: async (vendorData) => {
    const response = await apiClient.post('/vendors', vendorData);
    return response.data;
  },

  update: async (vendorId, vendorData) => {
    const response = await apiClient.put(`/vendors/${vendorId}`, vendorData);
    return response.data;
  },

  updateCategory: async (vendorId, category) => {
    const response = await apiClient.patch(`/vendors/${vendorId}/category?category=${category}`);
    return response.data;
  },

  delete: async (vendorId) => {
    const response = await apiClient.delete(`/vendors/${vendorId}`);
    return response.data;
  },

  // Reviews
  getReviews: async (vendorId) => {
    const response = await apiClient.get(`/vendors/${vendorId}/reviews`);
    return response.data;
  },

  createReview: async (vendorId, reviewData) => {
    const response = await apiClient.post(`/vendors/${vendorId}/reviews`, reviewData);
    return response.data;
  },

  // Favorites
  getFavorites: async () => {
    const response = await apiClient.get('/vendors/favorites/list');
    return response.data;
  },

  toggleFavorite: async (vendorId) => {
    const response = await apiClient.post(`/vendors/${vendorId}/favorite`);
    return response.data;
  },

  isFavorite: async (vendorId) => {
    const response = await apiClient.get(`/vendors/${vendorId}/is-favorite`);
    return response.data;
  },

  // Staffing Requests
  sendRequest: async (vendorId, requestData) => {
    const response = await apiClient.post(`/vendors/${vendorId}/request`, requestData);
    return response.data;
  },

  getMyRequests: async () => {
    const response = await apiClient.get('/staffing-requests');
    return response.data;
  },

  // Portfolio (for claimed vendors)
  updatePortfolio: async (vendorId, portfolioData) => {
    const response = await apiClient.put(`/vendors/${vendorId}/portfolio`, portfolioData);
    return response.data;
  },

  // Review Response (for claimed vendors)
  respondToReview: async (vendorId, reviewId, response) => {
    const res = await apiClient.post(`/vendors/${vendorId}/reviews/${reviewId}/respond`, { response });
    return res.data;
  },
};


// ==================== Claims API (Hybrid Freemium) ====================

export const claimsAPI = {
  // Submit a claim request
  submitClaim: async (claimData) => {
    const response = await apiClient.post('/claims/request', claimData);
    return response.data;
  },

  // Verify email
  verifyEmail: async (claimId, token) => {
    const response = await apiClient.post(`/claims/${claimId}/verify-email?token=${token}`);
    return response.data;
  },

  // Get my claims
  getMyClaims: async () => {
    const response = await apiClient.get('/claims/my-claims');
    return response.data;
  },

  // Admin: Get pending claims
  getPendingClaims: async () => {
    const response = await apiClient.get('/claims/pending');
    return response.data;
  },

  // Admin: Approve claim
  approveClaim: async (claimId) => {
    const response = await apiClient.put(`/claims/${claimId}/approve`);
    return response.data;
  },

  // Admin: Reject claim
  rejectClaim: async (claimId, reason = '') => {
    const response = await apiClient.put(`/claims/${claimId}/reject?reason=${encodeURIComponent(reason)}`);
    return response.data;
  },
};


// ==================== Subscriptions API (Lead Generation) ====================

export const subscriptionAPI = {
  // Get available plans
  getPlans: async () => {
    const response = await apiClient.get('/subscriptions/plans');
    return response.data;
  },

  // Create subscription
  createSubscription: async (vendorId, planType) => {
    const response = await apiClient.post('/subscriptions/create', { vendorId, planType });
    return response.data;
  },

  // Get my subscription for a vendor
  getMySubscription: async (vendorId) => {
    const response = await apiClient.get(`/subscriptions/my-subscription/${vendorId}`);
    return response.data;
  },
};


// ==================== Leads API ====================

export const leadsAPI = {
  // Get leads for a vendor
  getVendorLeads: async (vendorId) => {
    const response = await apiClient.get(`/leads/vendor/${vendorId}`);
    return response.data;
  },

  // Get analytics for a vendor
  getAnalytics: async (vendorId) => {
    const response = await apiClient.get(`/leads/analytics/${vendorId}`);
    return response.data;
  },
};

// ==================== Venue Directory API (Event Planner Feature) ====================

export const venueAPI = {
  getTypes: async () => {
    const response = await apiClient.get('/venues/types');
    return response.data;
  },

  getAmenities: async () => {
    const response = await apiClient.get('/venues/amenities');
    return response.data;
  },

  getAll: async (venueType = null, search = null, minCapacity = null, maxCapacity = null) => {
    const params = new URLSearchParams();
    if (venueType && venueType !== 'all') params.append('venue_type', venueType);
    if (search) params.append('search', search);
    if (minCapacity) params.append('min_capacity', minCapacity);
    if (maxCapacity) params.append('max_capacity', maxCapacity);
    const response = await apiClient.get(`/venues?${params.toString()}`);
    return response.data;
  },

  getById: async (venueId) => {
    const response = await apiClient.get(`/venues/${venueId}`);
    return response.data;
  },

  create: async (venueData) => {
    const response = await apiClient.post('/venues', venueData);
    return response.data;
  },

  update: async (venueId, venueData) => {
    const response = await apiClient.put(`/venues/${venueId}`, venueData);
    return response.data;
  },

  delete: async (venueId) => {
    const response = await apiClient.delete(`/venues/${venueId}`);
    return response.data;
  },

  // Reviews
  getReviews: async (venueId) => {
    const response = await apiClient.get(`/venues/${venueId}/reviews`);
    return response.data;
  },

  createReview: async (venueId, reviewData) => {
    const response = await apiClient.post(`/venues/${venueId}/reviews`, reviewData);
    return response.data;
  },

  // Favorites
  getFavorites: async () => {
    const response = await apiClient.get('/venues/favorites/list');
    return response.data;
  },

  toggleFavorite: async (venueId) => {
    const response = await apiClient.post(`/venues/${venueId}/favorite`);
    return response.data;
  },
};


// ==================== Vendor Claims API ====================

export const vendorClaimsAPI = {
  // Request to claim a vendor
  requestClaim: async (vendorId, businessEmail, businessPhone, ownerName, verificationNote = null) => {
    const response = await apiClient.post('/vendor-claims/request', {
      vendorId,
      businessEmail,
      businessPhone,
      ownerName,
      verificationNote
    });
    return response.data;
  },

  // Verify claim with email code
  verifyClaim: async (claimId, verificationCode) => {
    const response = await apiClient.post(`/vendor-claims/${claimId}/verify`, {
      verificationCode
    });
    return response.data;
  },

  // Get my claims
  getMyClaims: async () => {
    const response = await apiClient.get('/vendor-claims/my-claims');
    return response.data;
  },

  // Admin: Get pending claims
  getPendingClaims: async () => {
    const response = await apiClient.get('/vendor-claims/pending');
    return response.data;
  },

  // Admin: Approve/reject claim
  processClaim: async (claimId, approved, adminNote = null) => {
    const response = await apiClient.post(`/vendor-claims/${claimId}/approve`, {
      approved,
      adminNote
    });
    return response.data;
  },

  // Upgrade to premium
  upgradeToPremium: async (vendorId, plan) => {
    const response = await apiClient.post(`/vendor-claims/${vendorId}/upgrade-premium`, {
      plan
    });
    return response.data;
  },

  // Add portfolio image
  addPortfolioImage: async (vendorId, url, caption = null) => {
    const response = await apiClient.post(`/vendor-claims/${vendorId}/portfolio`, {
      url,
      caption
    });
    return response.data;
  },

  // Delete portfolio image
  deletePortfolioImage: async (vendorId, imageId) => {
    const response = await apiClient.delete(`/vendor-claims/${vendorId}/portfolio/${imageId}`);
    return response.data;
  },

  // Respond to review
  respondToReview: async (vendorId, reviewId, response) => {
    const res = await apiClient.post(`/vendor-claims/${vendorId}/respond-review`, {
      reviewId,
      response
    });
    return res.data;
  },

  // Get vendor analytics
  getAnalytics: async (vendorId) => {
    const response = await apiClient.get(`/vendor-claims/${vendorId}/analytics`);
    return response.data;
  },

  // Send claim invite email
  sendClaimInvite: async (vendorId, email) => {
    const response = await apiClient.post('/vendor-claims/send-claim-invite', { vendorId, email });
    return response.data;
  },

  // Check invite cooldown
  canSendInvite: async (vendorId) => {
    const response = await apiClient.get(`/vendor-claims/can-send-invite/${vendorId}`);
    return response.data;
  },

  // Reset invite cooldown
  resetInviteCooldown: async (vendorId) => {
    const response = await apiClient.post(`/vendor-claims/reset-invite-cooldown/${vendorId}`);
    return response.data;
  },
};


// ==================== Vendor Bookings ====================

export const vendorBookingsAPI = {
  // Planner: Send booking request
  sendRequest: async (bookingData) => {
    const response = await apiClient.post('/bookings/request', bookingData);
    return response.data;
  },

  // Vendor: Get incoming booking requests
  getIncoming: async (status = null) => {
    const url = status ? `/bookings/incoming?status=${status}` : '/bookings/incoming';
    const response = await apiClient.get(url);
    return response.data;
  },

  // Planner: Get sent booking requests
  getSent: async (status = null) => {
    const url = status ? `/bookings/sent?status=${status}` : '/bookings/sent';
    const response = await apiClient.get(url);
    return response.data;
  },

  // Vendor: Accept booking (1-click)
  accept: async (bookingId) => {
    const response = await apiClient.put(`/bookings/${bookingId}/accept`);
    return response.data;
  },

  // Vendor: Decline booking (1-click)
  decline: async (bookingId, reason = '') => {
    const response = await apiClient.put(`/bookings/${bookingId}/decline`, { declineReason: reason });
    return response.data;
  },

  // Planner: Mark booking as complete
  complete: async (bookingId, rating = null, review = null) => {
    const params = new URLSearchParams();
    if (rating) params.append('rating', rating);
    if (review) params.append('review', review);
    const url = `/bookings/${bookingId}/complete${params.toString() ? '?' + params.toString() : ''}`;
    const response = await apiClient.put(url);
    return response.data;
  },

  // Either: Cancel booking
  cancel: async (bookingId, reason = '') => {
    const response = await apiClient.put(`/bookings/${bookingId}/cancel?reason=${encodeURIComponent(reason)}`);
    return response.data;
  },

  // Get booking stats
  getStats: async () => {
    const response = await apiClient.get('/bookings/stats');
    return response.data;
  },

  // Get vendor's own profile (with logo from Yelp/Google)
  getMyVendorProfile: async () => {
    const response = await apiClient.get('/my-vendor-profile');
    return response.data;
  },
};


// ==================== Vendor Subscriptions ====================

export const vendorSubscriptionsAPI = {
  // Get available tiers
  getTiers: async () => {
    const response = await apiClient.get('/subscriptions/tiers');
    return response.data;
  },

  // Get my subscription
  getMyTier: async () => {
    const response = await apiClient.get('/subscriptions/my-tier');
    return response.data;
  },

  // Upgrade subscription
  upgrade: async (tier) => {
    const response = await apiClient.post(`/subscriptions/upgrade?tier=${tier}`);
    return response.data;
  },

  // Downgrade to starter
  downgrade: async () => {
    const response = await apiClient.post('/subscriptions/downgrade');
    return response.data;
  },

  // Get earnings summary
  getEarnings: async () => {
    const response = await apiClient.get('/earnings/summary');
    return response.data;
  },
};


// ==================== Vendor Agreement API ====================

export const vendorAgreementAPI = {
  // Get agreement text
  getText: async () => {
    const response = await apiClient.get('/vendor-agreement/text');
    return response.data;
  },

  // Accept agreement
  accept: async (data) => {
    const response = await apiClient.post('/vendor-agreement/accept', data);
    return response.data;
  },

  // Get agreement status for a vendor
  getStatus: async (vendorId) => {
    const response = await apiClient.get(`/vendor-agreement/status/${vendorId}`);
    return response.data;
  },

  // Get my signed agreements
  getMyAgreements: async () => {
    const response = await apiClient.get('/vendor-agreement/my-agreements');
    return response.data;
  },
};


// ==================== Unified Search API (Yelp + Google Combined) ====================

export const unifiedSearchAPI = {
  // Search vendors (combines Yelp & Google)
  searchVendors: async (location, params = {}) => {
    const queryParams = new URLSearchParams({ location });
    if (params.term) queryParams.append('term', params.term);
    if (params.category) queryParams.append('category', params.category);
    const response = await apiClient.post(`/search/vendors?${queryParams.toString()}`);
    return response.data;
  },

  // Search venues (combines Yelp & Google)
  searchVenues: async (location, params = {}) => {
    const queryParams = new URLSearchParams({ location });
    if (params.term) queryParams.append('term', params.term);
    if (params.venue_type) queryParams.append('venue_type', params.venue_type);
    const response = await apiClient.post(`/search/venues?${queryParams.toString()}`);
    return response.data;
  },

  // Search nearby (combines Yelp & Google)
  searchNearby: async (latitude, longitude, params = {}) => {
    const queryParams = new URLSearchParams();
    queryParams.append('latitude', latitude);
    queryParams.append('longitude', longitude);
    if (params.category) queryParams.append('category', params.category);
    if (params.search_type) queryParams.append('search_type', params.search_type);
    const response = await apiClient.post(`/search/nearby?${queryParams.toString()}`);
    return response.data;
  },

  // Import a business from search results
  importBusiness: async (source, sourceId, category = null, itemType = 'vendor') => {
    const queryParams = new URLSearchParams({ source, source_id: sourceId, item_type: itemType });
    if (category) queryParams.append('category', category);
    const response = await apiClient.post(`/search/import?${queryParams.toString()}`);
    return response.data;
  },
};
